import Axios from 'axios';

const MIME_TYPE_MAP: Record<string, { ext: string; type: string }> = {
  'image/png': { ext: 'png', type: 'image' },
  'image/jpeg': { ext: 'jpeg', type: 'image' },
  'image/jpg': { ext: 'jpg', type: 'image' },
  'application/pdf': { ext: 'pdf', type: 'pdf' },
  'application/msword': { ext: 'doc', type: 'msdoc' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
    ext: 'docx',
    type: 'msdoc',
  },
  'application/vnd.ms-powerpoint': { ext: 'ppt', type: 'msdoc' },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': {
    ext: 'pptx',
    type: 'msdoc',
  },
  'application/vnd.ms-excel': { ext: 'xls', type: 'msdoc' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {
    ext: 'xlsx',
    type: 'msdoc',
  },
  'video/mp4': { ext: 'mp4', type: 'video' },
  'video/quicktime': { ext: 'mov', type: 'video' },
  'video/x-m4v': { ext: 'm4v', type: 'video' },
  'video/x-msvideo': { ext: 'avi', type: 'video' },
  'video/webm': { ext: 'webm', type: 'video' },
  'audio/mpeg': { ext: 'mp3', type: 'audio' },
  'audio/wav': { ext: 'wav', type: 'audio' },
  'audio/x-wav': { ext: 'wav', type: 'audio' },
};

const EXT_TYPE_MAP: Record<string, { ext: string; type: string }> = {
  png: { ext: 'png', type: 'image' },
  jpg: { ext: 'jpg', type: 'image' },
  jpeg: { ext: 'jpeg', type: 'image' },
  gif: { ext: 'gif', type: 'image' },
  webp: { ext: 'webp', type: 'image' },
  bmp: { ext: 'bmp', type: 'image' },
  pdf: { ext: 'pdf', type: 'pdf' },
  doc: { ext: 'doc', type: 'msdoc' },
  docx: { ext: 'docx', type: 'msdoc' },
  ppt: { ext: 'ppt', type: 'msdoc' },
  pptx: { ext: 'pptx', type: 'msdoc' },
  xls: { ext: 'xls', type: 'msdoc' },
  xlsx: { ext: 'xlsx', type: 'msdoc' },
  mp4: { ext: 'mp4', type: 'video' },
  mov: { ext: 'mov', type: 'video' },
  m4v: { ext: 'm4v', type: 'video' },
  avi: { ext: 'avi', type: 'video' },
  webm: { ext: 'webm', type: 'video' },
  mp3: { ext: 'mp3', type: 'audio' },
  wav: { ext: 'wav', type: 'audio' },
};

export const COURSE_EXTRA_TYPE_MAP: Record<string, { ext?: string; type: string }> = {
  Image: { type: 'image' },
  Video: { type: 'video' },
  Audio: { type: 'audio' },
  PDF: { ext: 'pdf', type: 'pdf' },
  PPT: { ext: 'ppt', type: 'msdoc' },
  Word: { ext: 'docx', type: 'msdoc' },
};

export const getMediaFileInfoFromUrl = (url: string) => {
  const path = url.split('?')[0].split('#')[0].toLowerCase();
  const basename = path.split('/').pop() ?? '';
  const ext = basename.includes('.') ? (basename.split('.').pop() ?? '') : '';
  if (ext && EXT_TYPE_MAP[ext]) {
    return EXT_TYPE_MAP[ext];
  }
  // OSS 直传 key 常为 UUID（无扩展名）；类型未知，由预览组件 image→video 回退链判定
  if (basename && !basename.includes('.')) {
    return { ext: undefined, type: 'unsupported' };
  }
  return { ext: undefined, type: 'unsupported' };
};

export const getMediaFileInfo = async (url: string) => {
  // 浏览器内 OSS 不支持跨域 HEAD，直接用 URL 推断，避免 CORS 报错与无谓等待
  if (typeof window !== 'undefined') {
    return getMediaFileInfoFromUrl(url) as {
      ext: string | undefined;
      type: string;
    };
  }

  try {
    const {
      headers: { 'content-type': rawContentType },
    } = await Axios.head(url);
    const contentType = (rawContentType ?? '').split(';')[0].trim().toLowerCase();
    const mapped = MIME_TYPE_MAP[contentType];
    if (mapped) {
      return mapped as { ext: typeof mapped.ext; type: typeof mapped.type };
    }
  } catch {
    // OSS 等资源常不支持跨域 HEAD，回退到 URL 扩展名推断
  }

  return getMediaFileInfoFromUrl(url) as {
    ext: string | undefined;
    type: string;
  };
};

export type MediaFileInfo = Awaited<ReturnType<typeof getMediaFileInfo>>;
export type MediaFileType = MediaFileInfo['type'];
export type MediaFileExt = MediaFileInfo['ext'];

export type MediaLoadMode = 'oss' | 'raw' | 'video';

/** 媒体排序权重：图片优先，其次未知类型，再视频，最后其他 */
export function getMediaSortPriority(url: string): number {
  const { type } = getMediaFileInfoFromUrl(url);
  switch (type) {
    case 'image':
      return 0;
    case 'unsupported':
      return 1;
    case 'video':
      return 2;
    default:
      return 3;
  }
}

export type MediaWithUrl = { url: string; mediaId?: string; priority?: number };

/** 将媒体列表按「图片优先」排序，同类型内按 priority 升序 */
export function sortMediasImageFirst<T extends MediaWithUrl>(medias: T[]): T[] {
  return [...medias].sort((a, b) => {
    const priorityDiff = getMediaSortPriority(a.url) - getMediaSortPriority(b.url);
    if (priorityDiff !== 0) {
      return priorityDiff;
    }
    return (a.priority ?? 0) - (b.priority ?? 0);
  });
}

/** 从媒体列表中选取最适合做缩略图预览的 URL（优先图片，同步版） */
export function pickPreviewMediaUrl(medias: MediaWithUrl[]): string {
  const sorted = sortMediasImageFirst(medias);
  const url = sorted[0]?.url ?? '';
  return url ? url.replace(/^http:/, 'https:') : '';
}

const secureMediaUrl = (url: string) => url.replace(/^http:/, 'https:');

/** 通过浏览器加载探测无扩展名媒体的真实类型 */
export function probeMediaTypeFromUrl(url: string): Promise<'image' | 'video'> {
  const src = secureMediaUrl(url);
  return new Promise((resolve) => {
    const img = new Image();
    const tryVideo = () => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      const timer = window.setTimeout(() => resolve('video'), 8000);
      const finish = (type: 'image' | 'video') => {
        window.clearTimeout(timer);
        resolve(type);
      };
      video.onloadedmetadata = () => finish('video');
      video.onerror = () => finish('video');
      video.src = src;
    };
    img.onload = () => resolve('image');
    img.onerror = tryVideo;
    img.src = src;
  });
}

function probedSortPriority(url: string, probed?: 'image' | 'video'): number {
  const { type } = getMediaFileInfoFromUrl(url);
  if (type === 'image') {
    return 0;
  }
  if (type === 'video') {
    return 2;
  }
  if (probed === 'image') {
    return 0;
  }
  if (probed === 'video') {
    return 2;
  }
  return 1;
}

/**
 * 异步解析媒体列表，无扩展名 UUID 会探测真实类型，确保图片排在视频前。
 */
export async function resolveMediasImageFirst<T extends MediaWithUrl>(
  medias: T[],
): Promise<T[]> {
  if (medias.length <= 1) {
    return sortMediasImageFirst(medias);
  }

  const probedTypes = await Promise.all(
    medias.map(async (media) => {
      const { type } = getMediaFileInfoFromUrl(media.url);
      if (type === 'image' || type === 'video') {
        return { media, probed: type as 'image' | 'video' };
      }
      const probed = await probeMediaTypeFromUrl(media.url);
      return { media, probed };
    }),
  );

  return probedTypes
    .sort((a, b) => {
      const priorityDiff =
        probedSortPriority(a.media.url, a.probed) - probedSortPriority(b.media.url, b.probed);
      if (priorityDiff !== 0) {
        return priorityDiff;
      }
      return (a.media.priority ?? 0) - (b.media.priority ?? 0);
    })
    .map(({ media }) => media);
}

/** 异步选取缩略图 URL（优先图片） */
export async function pickPreviewMediaUrlAsync(medias: MediaWithUrl[]): Promise<string> {
  const sorted = await resolveMediasImageFirst(medias);
  return pickPreviewMediaUrl(sorted);
}

/**
 * 推断 AssignmentMediaPreview 的 mediaTypeHint（与学院端 AssignmentMediaViewer 一致）。
 * - 已知 image/video：直接传入
 * - 无扩展名 UUID：不传 hint，走 oss → raw → video 回退链（优先尝试图片）
 */
export function getAssignmentMediaTypeHint(
  url: string,
  _mode: 'thumbnail' | 'full' = 'full',
): MediaFileType | undefined {
  const { type } = getMediaFileInfoFromUrl(url);
  if (type === 'video' || type === 'image') {
    return type;
  }
  return undefined;
}

/** 推断预览初始加载模式：优先用已知类型，否则从 URL 扩展名；无扩展名时优先尝试 video（OSS UUID 直传常见）。 */
export function inferInitialLoadMode(
  url: string,
  mediaTypeHint?: MediaFileType,
): MediaLoadMode {
  if (mediaTypeHint === 'video') {
    return 'video';
  }
  if (mediaTypeHint === 'image') {
    return 'oss';
  }

  const fromUrl = getMediaFileInfoFromUrl(url);
  if (fromUrl.type === 'video') {
    return 'video';
  }
  if (fromUrl.type === 'image') {
    return 'oss';
  }

  const basename = url.split('?')[0].split('#')[0].split('/').pop() ?? '';
  if (!basename.includes('.')) {
    // 无扩展名 UUID：先试图片（OSS 处理），失败再回退 raw / video
    return 'oss';
  }

  return 'oss';
}
